<template>
  <transition name="wf-modal">
  <div
    v-if="visible"
    class="fixed inset-0 bg-gray-600/50 flex items-center justify-center z-50"
    @click="$emit('close')"
  >
    <div class="wf-modal-panel bg-white rounded-lg p-6 max-w-2xl w-full shadow-xl" @click.stop>
      <h3 class="text-lg font-medium text-gray-900 mb-1">New Data Attribute</h3>
      <p class="text-xs text-gray-400 mb-4">
        A data attribute is a reusable data primitive: write a SQL query, and it
        becomes a building block you can compose into workflows.
      </p>

      <div class="space-y-4">
        <!-- Name -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input
            v-model.trim="form.name"
            type="text"
            placeholder="e.g. IP discharges (14-day)"
            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
        </div>

        <!-- Type -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <select v-model="form.attribute_type" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
            <option value="categorical">Categorical (binary 0/1)</option>
            <option value="continuous">Continuous (numeric)</option>
            <option value="multi_categorical">Multi-categorical</option>
            <option value="date">Date</option>
          </select>
        </div>

        <!-- Data source: which warehouse this DA's SQL runs against. Default is
             Kairon EDW; an external connection runs the SQL on the customer's
             own warehouse. Only shown when the tenant has external sources. -->
        <div v-if="externalSources.length">
          <label class="block text-sm font-medium text-gray-700 mb-1">Data source</label>
          <select v-model="form.data_source_id" class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
            <option :value="null">Kairon EDW (default)</option>
            <option
              v-for="ds in externalSources"
              :key="ds.id"
              :value="ds.id"
              :disabled="!ds.is_authorized"
            >
              {{ ds.name }}{{ ds.is_authorized ? '' : ' (not connected)' }}
            </option>
          </select>
          <p class="mt-1 text-xs text-gray-400">
            Runs this attribute's SQL against the selected warehouse.
          </p>
        </div>

        <!-- SQL editor -->
        <div>
          <div class="flex items-center justify-between mb-1">
            <label class="block text-sm font-medium text-gray-700">SQL query</label>
            <button
              type="button"
              class="btn-link text-xs"
              :disabled="!form.sql_query || testing"
              @click="handleTest"
            >
              {{ testing ? 'Testing…' : 'Test' }}
            </button>
          </div>
          <textarea
            v-model="form.sql_query"
            rows="10"
            spellcheck="false"
            placeholder="SELECT BENE_MBI_ID, 1 AS VALUE FROM ALL_PAYER_TABLES.… WHERE TENANT_ID = {tenant_id}"
            class="w-full border border-gray-300 rounded-md px-3 py-2 text-xs font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-dark/60 transition"
          ></textarea>
          <p class="mt-1 text-xs text-gray-400">
            Return <span class="font-mono">BENE_MBI_ID</span> plus a value column.
            Use <span class="font-mono">{tenant_id}</span> as a placeholder.
          </p>
        </div>

        <!-- Test result: sample rows or error -->
        <div
          v-if="testResult"
          class="rounded-md border p-3 text-xs"
          :class="testResult.success ? 'border-green-200 bg-green-50/60' : 'border-red-200 bg-red-50/60'"
        >
          <template v-if="testResult.success">
            <div class="font-medium text-green-700 mb-1">
              ✓ Ran successfully — {{ testResult.row_count?.toLocaleString?.() ?? testResult.row_count }} rows
            </div>
            <div v-if="sampleColumns.length" class="font-mono text-[11px] text-slate-600 break-all">
              columns: {{ sampleColumns.join(', ') }}
            </div>
          </template>
          <div v-else class="text-red-dark">✕ {{ testResult.error || 'Query failed.' }}</div>
        </div>

        <!-- Save error (injection check / validation) -->
        <p v-if="saveError" class="text-xs text-red-dark">{{ saveError }}</p>
      </div>

      <div class="mt-6 flex justify-end gap-2">
        <button class="btn-secondary" @click="$emit('close')">Cancel</button>
        <button class="btn-primary" :disabled="!canSave || saving" @click="handleSave">
          {{ saving ? 'Saving…' : 'Create' }}
        </button>
      </div>
    </div>
  </div>
  </transition>
</template>

<script>
import axios from 'axios';

const ATTR_BASE = '/api/impact_analytics/attributes/';
const SOURCE_BASE = '/api/impact_analytics/data-sources/';

export default {
  name: 'DataAttributeBuilderModal',

  props: {
    visible: { type: Boolean, default: false },
  },

  emits: ['close', 'created'],

  data() {
    return {
      form: { name: '', attribute_type: 'categorical', sql_query: '', data_source_id: null },
      testing: false,
      testResult: null,
      saving: false,
      saveError: null,
      // External (non-system) data sources the DA can run against; empty for
      // tenants with only Kairon EDW, in which case the selector is hidden.
      externalSources: [],
    };
  },

  computed: {
    canSave() {
      return this.form.name && this.form.sql_query;
    },
    sampleColumns() {
      const rows = this.testResult && this.testResult.sample_rows;
      return rows && rows.length ? Object.keys(rows[0]) : [];
    },
  },

  watch: {
    visible(open) {
      if (open) {
        this.reset();
        this.fetchSources();
      }
    },
  },

  methods: {
    reset() {
      this.form = { name: '', attribute_type: 'categorical', sql_query: '', data_source_id: null };
      this.testing = false;
      this.testResult = null;
      this.saving = false;
      this.saveError = null;
    },

    async fetchSources() {
      // Offer only external (non-system) sources; Kairon EDW is the default
      // option and doesn't need a row. Silent on error — the selector just
      // stays hidden and DAs default to Kairon EDW.
      try {
        const { data } = await axios.get(SOURCE_BASE);
        const list = Array.isArray(data) ? data : (data.results || []);
        this.externalSources = list.filter(ds => !ds.is_system);
      } catch {
        this.externalSources = [];
      }
    },

    async handleTest() {
      // Run the SQL server-side (LIMIT-sampled) so the user sees it works and
      // what it returns BEFORE creating the attribute — the Tableau "preview
      // your extract" step. Never mutates anything.
      this.testing = true;
      this.testResult = null;
      try {
        const { data } = await axios.post(`${ATTR_BASE}test-sql/`, {
          sql_query: this.form.sql_query,
          attribute_type: this.form.attribute_type,
          data_source_id: this.form.data_source_id,
        });
        this.testResult = data;
      } catch (e) {
        this.testResult = { success: false, error: e.response?.data?.error || 'Test failed.' };
      } finally {
        this.testing = false;
      }
    },

    async handleSave() {
      // POST to the existing DataAttribute endpoint — the serializer runs the
      // mandatory injection check + output-column discovery on save, so a
      // rejected SQL surfaces here as a 400 with the reason.
      this.saving = true;
      this.saveError = null;
      try {
        const { data } = await axios.post(ATTR_BASE, {
          name: this.form.name,
          attribute_type: this.form.attribute_type,
          sql_query: this.form.sql_query,
          data_source_id: this.form.data_source_id,
        });
        this.$emit('created', data);
        this.$emit('close');
      } catch (e) {
        const d = e.response?.data;
        this.saveError = d?.sql_query || d?.error || d?.detail || 'Could not create the data attribute.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Shared modal entrance so the builders in this module feel like one system. */
.wf-modal-enter-active, .wf-modal-leave-active { transition: opacity 0.18s ease; }
.wf-modal-enter-from, .wf-modal-leave-to { opacity: 0; }
.wf-modal-enter-active .wf-modal-panel { transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease; }
.wf-modal-enter-from .wf-modal-panel { transform: translateY(8px) scale(0.97); opacity: 0; }

@media (prefers-reduced-motion: reduce) {
  .wf-modal-enter-active, .wf-modal-leave-active,
  .wf-modal-enter-active .wf-modal-panel { transition: none; }
}
</style>
